import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { IconButton } from "@mui/material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DriveProvider from "../../providers/DriveProvider";
import AuthService from "../../services/AuthService";
import FileRenameDialog from "../FileRenameDialog";
import FolderRenameDialog from "../FolderRenameDialog";
import FileViewModal from "../FileViewModal";
import ImageEditorDialog from "../ImageEditorDialog";
import DriveSidebar from "../FileTree";
import TopBar from "../TopBar";
import FilesAndFolders from "../FilesAndFoldersTbl";
import NewPatient from "../NewPatient";
import MyPatients from "../MyPatients";
import styles from "./driveLayout.module.css";

const theme = createTheme();
const MOBILE_BP = 900;

export default function DriveLayout() {
  const isMobile = () => window.innerWidth <= MOBILE_BP;

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile());
  const [activeView, setActiveView]   = useState('drive');
  const [patients, setPatients]       = useState([]);
  const [openPatient, setOpenPatient] = useState(null);

  // On resize: open sidebar on desktop, close on mobile
  useEffect(() => {
    const onResize = () => {
      if (!isMobile()) setSidebarOpen(true);
      else setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeSidebarOnMobile = useCallback(() => {
    if (isMobile()) setSidebarOpen(false);
  }, []);

  const handlePatientSaved = (patient) => {
    setPatients(prev => {
      const exists = prev.find(p => p.id === patient.id);
      if (exists) return prev.map(p => p.id === patient.id ? patient : p);
      return [patient, ...prev];
    });
    setOpenPatient(null);
    setActiveView('myPatients');
  };

  const handleOpenPatient = (patient) => {
    setOpenPatient(patient);
    setActiveView('newPatient');
  };

  const handleNavigate = (view) => {
    setActiveView(view);
    closeSidebarOnMobile();
  };

  useEffect(() => {
    const h = setInterval(() => AuthService.tokenRefresh(), 3600000);
    return () => clearInterval(h);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <DriveProvider>
        <CssBaseline />
        <FileRenameDialog />
        <FolderRenameDialog />
        <FileViewModal />
        <ImageEditorDialog />

        <div className={styles.shell}>

          {/* Mobile backdrop */}
          <div
            className={`${styles.backdrop} ${sidebarOpen && isMobile() ? styles.backdropVisible : ""}`}
            onClick={() => setSidebarOpen(false)}
          />

          {/* Sidebar */}
          <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ""}`}>
            <DriveSidebar
              expanded={sidebarOpen}
              onToggle={() => setSidebarOpen(v => !v)}
              onNavigate={handleNavigate}
            />
          </aside>

          {/* Main area */}
          <div className={styles.right}>
            <header className={styles.topbar}>
              {/* Hamburger — only visible on mobile */}
              <IconButton
                className={styles.menuBtn}
                onClick={() => setSidebarOpen(v => !v)}
                size="small"
                sx={{ color: "var(--text-2)", display: { xs: "flex", md: "none" } }}
              >
                <MenuOutlinedIcon />
              </IconButton>

              <TopBar sidebarOpen={sidebarOpen} onMenuToggle={() => setSidebarOpen(v => !v)} />
            </header>

            <main className={styles.main}>
              {activeView === 'newPatient' ? (
                <NewPatient onPatientSaved={handlePatientSaved} initialPatient={openPatient} />
              ) : activeView === 'myPatients' ? (
                <MyPatients patients={patients} setPatients={setPatients} onOpenPatient={handleOpenPatient} />
              ) : (
                <FilesAndFolders />
              )}
            </main>
          </div>
        </div>
      </DriveProvider>
    </ThemeProvider>
  );
}
